import React, {useEffect, useRef, useState} from 'react';
import {Row, Col} from 'reactstrap';
import {Field} from 'react-final-form';
import Select from "react-select";
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

const HIDE_CSV_PROGRAM_REQUEST_IDS = [3, 4, 9]; //csvimport types for hiding certain requests from setup

const SetupFields = ({setups, setupsFull, setTypeChanged, typeChanged, savedSettings, csvImportType}) => {

    if (!setups) return null;

    if (!setups.constructor === Object) {
        console.log("setups is not a valid object");
        return null;
    }
    if (Object.keys(setups).length === 0) {
        console.log("setups object is empty");
        return null;
    }

    const shouldHide = (formRequest) => {
        if (
            formRequest === 'CSVProgramRequest' &&
            HIDE_CSV_PROGRAM_REQUEST_IDS.includes(csvImportType?.id)
        ) {
            return true;
        }
        return false;
    };

    return (
        <div>
            <h4 className="d-flex align-items-center">
                Setups
                <Tooltip
                    title="Configure additional import settings such as validation rules and field mappings."
                    arrow
                    placement="right"
                >
                    <IconButton size="small" style={{padding: 4, marginLeft: 6}}>
                        <InfoOutlinedIcon style={{fontSize: 18}}/>
                    </IconButton>
                </Tooltip>
            </h4>

            {
                Object.keys(setups).filter(formRequest => !shouldHide(formRequest)).map((formRequest, i) =>
                    <div key={i}>
                        <FormSetupField {...{
                            formRequest,
                            setups,
                            setupsFull,
                            typeChanged,
                            savedSettings,
                            setTypeChanged
                        }}/>
                    </div>
                )
            }
        </div>
    );
};

export default SetupFields;

const RenderSetupField = ({
                              formRequest,
                              name,
                              rule,
                              typeChanged,
                              setTypeChanged,
                              savedSettings
                          }) => {

    const [selected, setSelected] = useState(null);
    const inputRef = useRef(null);
    const onChangeRef = useRef(null); // captures react-final-form's input.onChange

    const onSelectChange = (selectedOption, input) => {
        if (input.name === 'setups[UserRequest][type]') {
            if (selectedOption && typeChanged === false && input.value.value !== selectedOption.value) {
                setTypeChanged(selectedOption.value);
            }
        }
        setSelected(selectedOption);
        input.onChange(selectedOption);
    };

    useEffect(() => {
        // Only clear fields that belong to UserRequest when its type changes
        if (typeChanged !== false && name !== 'type' && formRequest === 'UserRequest') {
            setSelected(null);
            if (inputRef.current) {
                inputRef.current.clearValue();
            }
            // Also clear the react-final-form state so form POST is correct
            if (onChangeRef.current) {
                onChangeRef.current(null);
            }
        }

        // Restore saved values only when typeChanged is false (initial load)
        if (Object.keys(savedSettings).length > 0 && typeChanged === false) {
            Object.keys(savedSettings['setups']).forEach((item) => {
                Object.keys(savedSettings['setups'][item]).forEach((subItem) => {
                    if (subItem === name) {
                        const savedValue = savedSettings['setups'][item][subItem];
                        const matches = rule.match(/mustMatchWith\[([\s\S]*?)\]/);
                        if (matches?.[1]) {
                            const options = matches[1]
                                .split('|')
                                .map(opt => opt.trim())
                                .filter(Boolean)
                                .map(opt => ({value: opt, label: opt}));
                            const found = options.find(o => o.value === savedValue);
                            if (found) {
                                setSelected(found);
                                // Sync back to react-final-form state so form POST includes the value
                                if (onChangeRef.current) {
                                    onChangeRef.current(found);
                                }
                            }
                        }
                    }
                });
            });
        }
    }, [savedSettings, typeChanged]);

    if (rule.indexOf('mustMatchWith') === -1) {
        return <span>:{name}-field</span>;
    }

    const matches = rule.match(/mustMatchWith\[([\s\S]*?)\]/);
    if (!matches || !matches[1]) {
        return <span>:{name}-field</span>;
    }

    const options = matches[1]
        .split('|')
        .filter(Boolean)
        .map(opt => ({value: opt.trim(), label: opt.trim()}));

    return (
        <Field name={`setups[${formRequest}][${name}]`} key={name}>
            {({input, meta}) => {
                // Capture onChange on every render so useEffect always has latest reference
                onChangeRef.current = input.onChange;
                return (
                    <div>
                        <Select
                            options={options}
                            clearable={false}
                            className="react-select"
                            placeholder={' - - - '}
                            classNamePrefix="react-select"
                            ref={inputRef}
                            value={selected}
                            onChange={(selectedOption) => onSelectChange(selectedOption, input)}
                        />
                        {meta.touched && meta.error &&
                            <span className="form__form-group-error">{meta.error}</span>
                        }
                    </div>
                );
            }}
        </Field>
    );
};

const FormSetupFieldGroup = ({
                                 formRequest, fields, field, typeChanged, setTypeChanged, setupsFull,
                                 savedSettings
                             }) => {
    const rule = fields[field];
    // hide fields that have no renderable rule
    if (!rule || (!rule.includes('mustMatchWith'))) {
        return null;
    }
    const setupsSearch = setupsFull[formRequest][field].split('|');
    const isRequired = setupsSearch.filter(item => {
        if (item === 'nullable') {
            return true;
        }
    }).length === 0;

    return (
        <Row
            key={`setupField-${field}`}
            className={field === 'type' ? 'type-row' : ''}
        >
            <Col className='col-md-3'>
                <h5>{field.toUpperCase()}{isRequired ? <b>*</b> : ''}</h5>
            </Col>
            <Col className='col-md-9 col-lg-7 col-xl-5'>
                <RenderSetupField
                    formRequest={formRequest}
                    name={field}
                    rule={fields[field]}
                    typeChanged={typeChanged}
                    setTypeChanged={setTypeChanged}
                    savedSettings={savedSettings}
                />
            </Col>
        </Row>
    );
};

const FormSetupField = ({
                            formRequest,
                            setups,
                            setupsFull,
                            typeChanged,
                            setTypeChanged,
                            savedSettings
                        }) => {

    return (
        <div>
            <Row>
                <Col md="12"><h5> {formRequest} Setup</h5></Col>
            </Row>
            {
                Object.keys(setups[formRequest]).map((field, j) =>
                    <div key={j}>
                        <FormSetupFieldGroup
                            formRequest={formRequest}
                            fields={setups[formRequest]}
                            field={field}
                            typeChanged={typeChanged}
                            setTypeChanged={setTypeChanged}
                            setupsFull={setupsFull}
                            savedSettings={savedSettings}
                        />
                    </div>
                )
            }
        </div>
    );
};